import { ReactNode, useRef } from "react";
import { IconifyIcon } from "@iconify/react";
import { SnackbarProvider, SnackbarKey } from "notistack";
import { useTheme } from "@mui/material/styles";
import { Box, GlobalStyles, Collapse } from "@mui/material";
import { ColorSchema } from "theme//palette";
import useAppTheme from "hooks/useAppTheme";
import Iconify from "components/iconify";
import { IconButtonAnimate } from "components/animate";

// ----------------------------------------------------------------------

function SnackbarStyles() {
  const theme = useTheme();

  const isLight = theme.palette.mode === "light";

  return (
    <GlobalStyles
      styles={{
        "#root": {
          "& .SnackbarContent-root": {
            width: "100%",
            padding: theme.spacing(1),
            margin: theme.spacing(0.25, 0),
            boxShadow: theme.customShadows.z8,
            borderRadius: theme.shape.borderRadius,
            color: isLight ? theme.palette.common.white : theme.palette.grey[800],
            backgroundColor: isLight ? theme.palette.grey[900] : theme.palette.common.white,
            "&.SnackbarItem-variantSuccess": {
              color: theme.palette.success.contrastText,
              backgroundColor: theme.palette.success.main,
            },
            "&.SnackbarItem-variantError": {
              color: theme.palette.error.contrastText,
              backgroundColor: theme.palette.error.main,
            },
            "&.SnackbarItem-variantWarning": {
              color: theme.palette.warning.contrastText,
              backgroundColor: theme.palette.warning.main,
            },
            "&.SnackbarItem-variantInfo": {
              color: theme.palette.info.contrastText,
              backgroundColor: theme.palette.info.main,
            },
            [theme.breakpoints.up("md")]: {
              minWidth: 240,
            },
          },
          "& .SnackbarItem-message": {
            padding: "0 !important",
            fontWeight: theme.typography.fontWeightMedium,
          },
          "& .SnackbarItem-action": {
            marginRight: 0,
            color: theme.palette.action.active,
            "& svg": { width: 20, height: 20 },
          },
        },
      }}
    />
  );
}

// ----------------------------------------------------------------------

type Props = {
  children: ReactNode;
};

export default function CustomSnackProvider({ children }: Props) {
  const { themeDirection } = useAppTheme();

  const isRTL = themeDirection === "rtl";

  const notistackRef = useRef<any>(null);

  const onClose = (key: SnackbarKey) => () => {
    notistackRef.current.closeSnackbar(key);
  };

  return (
    <>
      <SnackbarStyles />

      <SnackbarProvider
        ref={notistackRef}
        dense
        maxSnack={5}
        autoHideDuration={3000}
        TransitionComponent={isRTL ? Collapse : undefined}
        variant="success" // Set default variant
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        iconVariant={{
          info: <SnackbarIcon icon="mdi:information" color="info" />,
          success: <SnackbarIcon icon="mdi:check-circle" color="success" />,
          warning: <SnackbarIcon icon="mdi:alert" color="warning" />,
          error: <SnackbarIcon icon="mdi:alert-octagon" color="error" />,
        }}
        // With close as default
        action={(key) => (
          <IconButtonAnimate size="small" onClick={onClose(key)} sx={{ p: 0.5 }}>
            <Iconify icon="mdi:close" color="white" />
          </IconButtonAnimate>
        )}
      >
        {children}
      </SnackbarProvider>
    </>
  );
}

// ----------------------------------------------------------------------

type SnackbarIconProps = {
  icon: IconifyIcon | string;
  color: ColorSchema;
};

function SnackbarIcon({ icon, color }: SnackbarIconProps) {
  return (
    <Box
      component="span"
      sx={{
        mr: 1.5,
        width: 40,
        height: 40,
        display: "flex",
        borderRadius: 1.5,
        alignItems: "center",
        justifyContent: "center",
        color: `${color}.contrastText`,
      }}
    >
      <Iconify icon={icon} width={24} height={24} />
    </Box>
  );
}
